import type {
  HealthcareService,
  Location,
  Reference,
} from "@medplum/fhirtypes";
import type { TFunction } from "i18next";

import type {
  HealthcareServicePatch,
  LocationService,
  LocationServicesChange,
  LocationServiceWarning,
  ServiceLocationAvailability,
  ServiceLocationsChange,
} from "@/types/clinic-configuration";
import { FHIR_EXTENSIONS } from "@/constants/fhir.constants";
import {
  buildVersionedPatchOperations,
  replaceExtensionRun,
} from "@/lib/utils/clinic-configuration/resource-patch";
import { getExtensions } from "@/lib/utils/fhir";
import { getReferenceId } from "@/lib/utils/helpers/fhir";
import { isBoolean } from "@/lib/utils/typeof";

import { orderLinkIds } from "./link-order";

const ALL_LOCATIONS_URL = FHIR_EXTENSIONS.HEALTHCARE_SERVICE_ALL_LOCATIONS;

/**
 * Kora resolves only `Location/<id>`. Any other reference keeps its raw
 * value, which matches no location, so it shows as unknown and is written
 * back as stored.
 */
const toLocationId = ({ reference = "" }: Reference): string =>
  getReferenceId(reference, "Location") ?? reference;

/**
 * Where Kora offers the service. The flag decides, read from the first
 * extension as Kora does: with `true` the list is not read at all.
 */
export const getServiceLocationAvailability = (
  service: HealthcareService
): ServiceLocationAvailability => {
  const [flag] = getExtensions(service, ALL_LOCATIONS_URL);

  if (!isBoolean(flag?.valueBoolean)) {
    return { kind: "unset" };
  }
  if (flag.valueBoolean) {
    return { kind: "all" };
  }

  return {
    kind: "some",
    locationIds: [...new Set((service.location ?? []).map(toLocationId))],
  };
};

/**
 * Stored references stay as written and in their order, so a save that
 * only adds a location changes nothing else. New ones go last.
 */
const toLocationReferences = (
  stored: Reference<Location>[],
  locationIds: string[]
): Reference<Location>[] => {
  const wanted = new Set(locationIds);
  const kept = stored.filter((reference) =>
    wanted.has(toLocationId(reference))
  );
  const keptIds = new Set(kept.map(toLocationId));
  const added = [...wanted]
    .filter((id) => !keptIds.has(id))
    .map((id) => ({ reference: `Location/${id}` }));

  return [...kept, ...added];
};

/**
 * `service` with its locations set to `change`, as a new object, before
 * `createPatch`. With `all`, the list goes: Kora does not read it, and a
 * stale one would only block deleting a location. FHIR has no empty
 * arrays, so an empty list removes the key.
 */
export const applyServiceLocations = <Service extends HealthcareService>(
  service: Service,
  change: ServiceLocationsChange
): Service => {
  const copy: Service = {
    ...service,
    extension: replaceExtensionRun(service.extension ?? [], ALL_LOCATIONS_URL, [
      { url: ALL_LOCATIONS_URL, valueBoolean: change.kind === "all" },
    ]),
  };
  const location =
    change.kind === "all"
      ? []
      : toLocationReferences(service.location ?? [], change.locationIds);

  if (location.length > 0) {
    copy.location = location;
  } else {
    delete copy.location;
  }

  return copy;
};

/**
 * `locationIds` in `locations` order, then the unknown ones in their own
 * order (`orderLinkIds`).
 */
export const orderLocationIds = (
  locationIds: string[],
  locations: Location[]
): string[] =>
  orderLinkIds(
    locationIds,
    locations.flatMap(({ id }) => (id ? [id] : []))
  );

/**
 * Whether a `some` service lists a location that is not in `locations`.
 * Kora drops such a service, while its count still reads like any other.
 */
export const hasUnknownServiceLocation = (
  availability: ServiceLocationAvailability,
  locations: Location[]
): boolean => {
  if (availability.kind !== "some") {
    return false;
  }

  const knownIds = new Set(locations.map(({ id }) => id));
  return availability.locationIds.some((id) => !knownIds.has(id));
};

/**
 * The Services table's "Sites" cell.
 */
export const formatServiceSites = (
  availability: ServiceLocationAvailability,
  t: TFunction
): string => {
  switch (availability.kind) {
    case "all":
      return t("All sites");
    case "unset":
      return t("Not set");
    case "some": {
      const count = availability.locationIds.length;
      return count === 0 ? t("None") : t("{{count}} sites", { count });
    }
  }
};

/**
 * Every service of `services` with whether Kora offers it at `locationId`.
 * `all` counts, whatever the list holds: Kora does not read it then.
 */
export const getLocationServices = (
  services: HealthcareService[],
  locationId: string
): LocationService[] =>
  services.flatMap((service) => {
    if (!service.id) {
      return [];
    }

    const availability = getServiceLocationAvailability(service);
    const isOffered =
      availability.kind === "all" ||
      (availability.kind === "some" &&
        availability.locationIds.includes(locationId));

    return [{ id: service.id, service, availability, isOffered }];
  });

/**
 * The Location card's selection as a change against what Kora offers there
 * now, so a save writes only the services the user changed.
 */
export const toLocationServicesChange = (
  offeredIds: string[],
  selectedIds: string[]
): LocationServicesChange => {
  const offered = new Set(offeredIds);
  const selected = new Set(selectedIds);

  return {
    checkedIds: selectedIds.filter((id) => !offered.has(id)),
    uncheckedIds: offeredIds.filter((id) => !selected.has(id)),
  };
};

/**
 * What the Location card says under a service, with its current check, so
 * the user sees what a save does to it before saving. Unchecking changes
 * the service for every location, which is what the notes are about.
 */
export const getLocationServiceWarning = (
  availability: ServiceLocationAvailability,
  isChecked: boolean,
  locationId: string,
  locations: Location[]
): LocationServiceWarning | undefined => {
  if (hasUnknownServiceLocation(availability, locations)) {
    return { kind: "unknownLocation" };
  }
  if (isChecked) {
    return undefined;
  }

  switch (availability.kind) {
    case "unset":
      return { kind: "unset" };
    case "all": {
      const count = locations.filter(
        ({ id }) => id && id !== locationId
      ).length;
      return count === 0
        ? { kind: "offeredNowhere" }
        : { kind: "otherLocationsOnly", count };
    }
    case "some":
      return availability.locationIds.length > 0 &&
        availability.locationIds.every((id) => id === locationId)
        ? { kind: "offeredNowhere" }
        : undefined;
  }
};

/**
 * What checking or unchecking `locationId` writes to one service, or
 * `undefined` when Kora already reads it that way. Unchecking an `all`
 * service lists every other location, since a location has no setting of
 * its own to turn off. Unknown references stay, as in the Service drawer.
 */
const toServiceLocationsChange = (
  availability: ServiceLocationAvailability,
  isChecked: boolean,
  locationId: string,
  locationIds: string[]
): ServiceLocationsChange | undefined => {
  if (availability.kind === "unset") {
    return isChecked ? { kind: "some", locationIds: [locationId] } : undefined;
  }
  if (availability.kind === "all") {
    return isChecked
      ? undefined
      : {
          kind: "some",
          locationIds: locationIds.filter((id) => id !== locationId),
        };
  }
  if (availability.locationIds.includes(locationId) === isChecked) {
    return undefined;
  }

  return {
    kind: "some",
    locationIds: isChecked
      ? [...availability.locationIds, locationId]
      : availability.locationIds.filter((id) => id !== locationId),
  };
};

/**
 * One patch per service the Location save changes. Each is built against
 * the `serviceList` copy the user saw and tests its version, so a service
 * written elsewhere since fails the whole save. `locationIds`: the clinic's
 * locations, for an `all` service unchecked here.
 */
export const buildLocationServicePatches = (
  services: HealthcareService[],
  locationIds: string[],
  locationId: string,
  { checkedIds, uncheckedIds }: LocationServicesChange
): HealthcareServicePatch[] => {
  const checked = new Set(checkedIds);
  const unchecked = new Set(uncheckedIds);

  return services.flatMap((service) => {
    const { id, meta } = service;
    if (!id || (!checked.has(id) && !unchecked.has(id))) {
      return [];
    }

    const change = toServiceLocationsChange(
      getServiceLocationAvailability(service),
      checked.has(id),
      locationId,
      locationIds
    );
    if (!change) {
      return [];
    }
    if (!meta?.versionId) {
      throw new Error(`HealthcareService "${id}" has no versionId.`);
    }

    return [
      {
        id,
        operations: buildVersionedPatchOperations(
          service,
          applyServiceLocations(service, change),
          meta.versionId
        ),
      },
    ];
  });
};
